import { Accordion, AccordionDetails, AccordionSummary, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next"
import { debounceFunction } from "../../../../utils/utilFunctions";
import { treeActions } from "../../../../redux/slices/treeSlice";
import { SectionHeader } from "../SectionHeader";
import SourceType from "../../../TemplateProperties/SourceType";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep, isEqual } from "lodash";
import { t } from "i18next";
import DefaultCheckbox from "../OutboundFieldFormattingOptions/DefaultCheckbox";

export const OutboundSequencePropertyForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //redux 
  const parentScreen = useContext(ParentScreenCtx);
  const queryParams = useSelector((state) => state.queryParams);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
  const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
  const templateHeader = rightTree.tree.templateHeader;
  const account = queryParams.account;

  //states
  const [sequenceName, setSequenceName] = useState('UNNAMED');
  const [description, setDescription] = useState('');
  const [sequenceGroup, setSequenceGroup] = useState('');
  const [fieldType, setFieldType] = useState('SELECT');
  const [constantValue, setConstantValue] = useState('');
  const [incrementBy, setIncrementBy] = useState('1');
  const [maxValue, setMaxValue] = useState('');
  const [type, setType] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const resetStates = () => {
    setSequenceName('UNNAMED');
    setSequenceGroup('');
    setFieldType('SELECT');
    setConstantValue('');
    setIncrementBy('');
    setMaxValue('');
    setType(false);
  }

  const createNewNodeStructure = () => {
    let newSelectedNode = cloneDeep(selectedNode);
    let updatedData = eachDeep(cloneDeep(translatedData), (value, key, parentArr, ctx) => {
      if (typeof value !== 'object') return;
      if (value?.nodeId && value.nodeId === selectedNode.nodeId) {
        //set node prop structure if it is not correct (mainly when editing controls after node creation)
        if (!value.SEQUENCE) {
          value.SEQUENCE = {
            "INCREMENT_BY": "",
            "MAX_VALUE": "",
            "NAME": "",
            "TYPE": "SINGLE"
          }
          newSelectedNode.SEQUENCE = {
            "INCREMENT_BY": "",
            "MAX_VALUE": "",
            "NAME": "",
            "TYPE": "SINGLE"
          }
        }
        if (!value.IN_ARG) {
          value.IN_ARG = [{
            "ARGNAME": "ID",
            "ARGTYPE": "IN",
            "FIELDTYPE": "ALPHA",
            "ICON_DISPLAY": "IN_ARG",
            "MAPNAME": "",
            "PARENT": "",
            "RECORD_TYPE": "",
            "SOURCE_TYPE": "",
            "VALUE": ""
          }]
          newSelectedNode.IN_ARG = [{
            "ARGNAME": "ID",
            "ARGTYPE": "IN",
            "FIELDTYPE": "ALPHA",
            "ICON_DISPLAY": "IN_ARG",
            "MAPNAME": "",
            "PARENT": "",
            "RECORD_TYPE": "",
            "SOURCE_TYPE": "",
            "VALUE": ""
          }]
        }
        if (!value.OUT_ARG) {
          value.OUT_ARG = [{
            "ARGNAME": "",
            "ARGTYPE": "OUT",
            "DESCRIPTION": "",
            "ICON_DISPLAY": "OUT_ARG",
            "MAPNAME": "",
            "RECORD_TYPE": "SEQUENCE",
            "SOURCE_TYPE": "OUTBOUND_ITEM"
          }]
          newSelectedNode.OUT_ARG = [{
            "ARGNAME": "",
            "ARGTYPE": "OUT",
            "DESCRIPTION": "",
            "ICON_DISPLAY": "OUT_ARG",
            "MAPNAME": "",
            "RECORD_TYPE": "SEQUENCE",
            "SOURCE_TYPE": "OUTBOUND_ITEM"
          }]
        }
        if (!value.EXECUTION_DETAILS) {
          value.EXECUTION_DETAILS = {
            "ICON_DISPLAY": "EXECUTION_DETAILS",
            "ARGTYPE": "DETAILS"
          }
          newSelectedNode.EXECUTION_DETAILS = {
            "ICON_DISPLAY": "EXECUTION_DETAILS",
            "ARGTYPE": "DETAILS"
          }
        }
      }
    });

    //prevent dispatch for no reason
    if (isEqual(translatedData, updatedData)) return;

    //set new trans data to updatedData.
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );

    //set selectedNode to match.
    dispatch(
      treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: newSelectedNode,
      })
    );
  };

  const setInitialInputVals = () => {
    //failsafe incase issue with selected node
    if (selectedNode) {
      let sn = selectedNode;
      if (!sn.SEQUENCE && !sn.IN_ARG && !sn.OUT_ARG && !sn.EXECUTION_DETAILS) {
        //if new node, reset all the states and return
        resetStates();
        createNewNodeStructure();
        return;
      }

      //Will be adding more datatypes to this condition. Starting with CSV.
      if (sn) {
        
        //custom field:
        //  NAME - name
        if (sn.NAME && sn.NAME !== "") {
          setSequenceName(sn.NAME);
        } else {
          setSequenceName("UNNAMED");
        }
        

        //sequence:
        //  NAME - group
        setSequenceGroup(sn.SEQUENCE?.NAME || "")

        //arg in:
        //  FIELDTYPE
        if (sn.IN_ARG && sn.IN_ARG[0] && sn.IN_ARG[0].FIELDTYPE) {
          setFieldType(sn.IN_ARG[0].FIELDTYPE);
        } else {
          setFieldType("SELECT");
        }

        //values saved in multple nested locations.
        //arg out:
        //  ARGNAME - name
        //  DESCRIPTION - name?
        //  MAPNAME - name

        //Increment by:
        //  SEQUENCE.INCREMENT_BY
        setIncrementBy(sn.SEQUENCE?.INCREMENT_BY || "1")

        //Max Value:
        //  SEQUENCE.MAX_VALUE
        setMaxValue(sn.SEQUENCE?.MAX_VALUE || "")

        //Increment only once:
        //  SEQUENCE.TYPE ("SINGLE"=true - "MULTI"=false)
        setType((sn.SEQUENCE?.TYPE && sn.SEQUENCE?.TYPE === "SINGLE") || false);

        if (sn.IN_ARG && sn.IN_ARG[0] && sn.IN_ARG[0].VALUE) {
          setConstantValue(sn.IN_ARG[0].VALUE || "");
        }
      }
    }
  }
  

  // clone tdata, loop thru and find curr node, 
  // set property to new val, then dispatch new tdata.
  const storeInputVal = (prop, newVal) => {
    let newSelectedNode = cloneDeep(selectedNode);
    let updatedData = eachDeep(cloneDeep(translatedData), (value, key, parentArr, ctx) => {
      if (typeof value !== 'object') return;
      if (value?.nodeId && value.nodeId === selectedNode.nodeId) {
        if (prop === "FIELDTYPE") {
          //constant is stored as "PROPERTY" in this component.
          

          if (newVal === "CONSTANT") {
            value.IN_ARG[0].FIELDTYPE = "PROPERTY";
            newSelectedNode.IN_ARG[0].FIELDTYPE = "PROPERTY";
            // value.PARENT = PARENT || undefined;
            // value.RECORD_TYPE = RECORD_TYPE
            // value.MAPNAME = MAPNAME;
            // value.ACTION = ACTION;
            // value.INDEX = INDEX;
            // value.REPITITION_INDEX = REPITITION_INDEX;

          } else {
            value.IN_ARG[0].FIELDTYPE = newVal;
          newSelectedNode.IN_ARG[0].FIELDTYPE = newVal;
          }
        } else if (prop === "NAME") {
          if (newVal === "") {
            value.title = `${t("SEQUENCE")}:`;
            value.NAME = "";
            newSelectedNode.title = `${t("SEQUENCE")}:`;
            newSelectedNode.NAME = "";
              value.OUT_ARG[0].MAPNAME = ""; 
              if (value?.OUT_ARG[0]?.ARGNAME || value?.OUT_ARG[0]?.ARGNAME === "") {
                delete value.OUT_ARG[0].ARGNAME;
              }
              newSelectedNode.OUT_ARG[0].MAPNAME = ""; 
              if (newSelectedNode?.OUT_ARG[0]?.ARGNAME || newSelectedNode?.OUT_ARG[0]?.ARGNAME === "") {
                delete newSelectedNode.OUT_ARG[0].ARGNAME;
              }
          } else {
            value.title = `${t("SEQUENCE")}: ${newVal}`;
            value.NAME = newVal;
            newSelectedNode.title = `${t("SEQUENCE")}: ${newVal}`;
            newSelectedNode.NAME = newVal;
            if (value.OUT_ARG && value.OUT_ARG[0]) {
              value.OUT_ARG[0].MAPNAME = newVal; 
              value.OUT_ARG[0].ARGNAME = newVal; 
              newSelectedNode.OUT_ARG[0].MAPNAME = newVal; 
              newSelectedNode.OUT_ARG[0].ARGNAME = newVal; 
            }
          }
        } else if (prop === "GROUP") {
          if (newVal === "") {
            if (value?.SEQUENCE?.NAME || value?.SEQUENCE?.NAME === "") {
              delete value.SEQUENCE.NAME;
            }
            if (newSelectedNode?.SEQUENCE?.NAME || newSelectedNode?.SEQUENCE?.NAME) {
              delete newSelectedNode.SEQUENCE.NAME;
            }
          } else {
            value.SEQUENCE.NAME = newVal;
            newSelectedNode.SEQUENCE.NAME = newVal;
          }
        } else if (prop === "VALUE") {
          if (newVal === "") {
            if (value.IN_ARG[0].VALUE || value.IN_ARG[0].VALUE === "") {
              delete value.IN_ARG[0].VALUE;
            }
            if (newSelectedNode?.IN_ARG[0].VALUE || newSelectedNode?.IN_ARG[0].VALUE === "") {
              delete newSelectedNode.IN_ARG[0].VALUE;
            }
          } else {
            value.IN_ARG[0].VALUE = newVal;
            newSelectedNode.IN_ARG[0].VALUE = newVal;
          }
        } else if (prop === "INCREMENT_BY") {
          if (newVal === "") {
            if (value?.SEQUENCE?.INCREMENT_BY || value?.SEQUENCE?.INCREMENT_BY === "") {
              delete value.SEQUENCE.INCREMENT_BY;
            }
            if (newSelectedNode?.SEQUENCE?.INCREMENT_BY || newSelectedNode?.SEQUENCE?.INCREMENT_BY === "") {
              delete newSelectedNode.SEQUENCE.INCREMENT_BY;
            }
          } else {            
            value.SEQUENCE.INCREMENT_BY = newVal;
            newSelectedNode.SEQUENCE.INCREMENT_BY = newVal;
          }
        } else if (prop === "MAX_VALUE") {
          if (newVal === "") {
            if (value?.SEQUENCE?.MAX_VALUE) {
              delete value.SEQUENCE.MAX_VALUE;
            }
            if (newSelectedNode?.SEQUENCE?.MAX_VALUE) {
              delete newSelectedNode.SEQUENCE.MAX_VALUE;
            }
          } else {
            value.SEQUENCE.MAX_VALUE = newVal;
            newSelectedNode.SEQUENCE.MAX_VALUE = newVal;
          }
        } else if (prop === "TYPE") {
          value.SEQUENCE.TYPE = newVal === true ? "SINGLE" : "MULTI";
          newSelectedNode.SEQUENCE.TYPE = newVal === true ? "SINGLE" : "MULTI";
        }
      }
    });
    
    //prevent dispatch for no reason
    if (isEqual(translatedData, updatedData)) return;
    
    //set new trans data to updatedData.
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );

    //set selectedNode to match.
    dispatch(
      treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: newSelectedNode,
      })
    );
  }

  let example = {
    "TYPE": "SEQUENCE",
    "NAME": "test-seq-char",
    "DESCRIPTION": "",
    "ICON_DISPLAY": "SEQUENCE",
    "IS_CACHEABLE": "TRUE",
    "SEQUENCE": {
        "INCREMENT_BY": "546",
        "MAX_VALUE": "789",
        "NAME": "1234567890abcdefghijklmnopqrst",
        "TYPE": "SINGLE"
    },
    "IN_ARG": [
        {
            "ARGNAME": "ID",
            "ARGTYPE": "IN",
            "FIELDTYPE": "ALPHA",
            "ICON_DISPLAY": "IN_ARG",
            "MAPNAME": "Volume ID",
            "PARENT": "NONE%1EOrder+Header",
            "RECORD_TYPE": "Detail Record",
            "SOURCE_TYPE": "INBOUND_DATA",
            "VALUE": "testconstval123"
        }
    ],
    "OUT_ARG": [
        {
            "ARGNAME": "test-seq-char",
            "ARGTYPE": "OUT",
            "DESCRIPTION": "test seq",
            "ICON_DISPLAY": "OUT_ARG",
            "MAPNAME": "test-seq-char",
            "RECORD_TYPE": "SEQUENCE",
            "SOURCE_TYPE": "OUTBOUND_ITEM"
        }
    ],
    "EXECUTION_DETAILS": {
        "ICON_DISPLAY": "EXECUTION_DETAILS",
        "ARGTYPE": "DETAILS"
    }
  }

  const sourceTypeValueChange = (values, sourceDropdownType) => {
    let newSelNode = false;
    const updatedTranslatedData = eachDeep(cloneDeep(translatedData), (obj) => {
     if ( obj && obj.key === selectedNode.key) {
      newSelNode = JSON.parse(JSON.stringify(selectedNode));
      //source type property mapping
      if (!obj.IN_ARG || !obj.IN_ARG[0]) {
        //if  in arg isnt in seq properties
        obj.IN_ARG = [{
          ARGNAME: "ID",
          ARGTYPE: "IN",
          FIELDTYPE: fieldType === "CONSTANT" ? "PROPERTY" : fieldType,
          ICON_DISPLAY: "IN_ARG",
        }];
        if (fieldType === "CONSTANT") obj.IN_ARG[0].VALUE = constantValue;
      }
      if (!newSelNode.IN_ARG || !newSelNode.IN_ARG[0]) {
        //if  in arg isnt in seq properties
        newSelNode.IN_ARG = [{
          ARGNAME: "ID",
          ARGTYPE: "IN",
          FIELDTYPE: fieldType === "CONSTANT" ? "PROPERTY" : fieldType,
          ICON_DISPLAY: "IN_ARG",
        }];
        if (fieldType === "CONSTANT") newSelNode.IN_ARG[0].VALUE = constantValue;
      }

      //determine dropdown selection
      if (sourceDropdownType === "INBOUND_DATA") {
        //inbound item
        if (values.MAPNAME === "" && values.RECORD_TYPE === "") {
          //if user selects inbound item but doesnt choose a node from inbound tree
          obj.IN_ARG[0] = {
            ARGNAME: "ID",
            ARGTYPE: "IN",
            FIELDTYPE: fieldType === "CONSTANT" ? "PROPERTY" : fieldType,
            ICON_DISPLAY: "IN_ARG",
          }
          newSelNode.IN_ARG[0] = {
            ARGNAME: "ID",
            ARGTYPE: "IN",
            FIELDTYPE: fieldType === "CONSTANT" ? "PROPERTY" : fieldType,
            ICON_DISPLAY: "IN_ARG",
          }
        } else {
          //if user chooses node from inbouind tree
          obj.IN_ARG[0].SOURCE_TYPE = "INBOUND_DATA";
          newSelNode.IN_ARG[0].SOURCE_TYPE = "INBOUND_DATA";
        }
       
      } else if (sourceDropdownType === "OUTBOUND_ITEM") {
        //outbound item
        obj.IN_ARG[0].SOURCE_TYPE = "OUTBOUND_ITEM";
        newSelNode.IN_ARG[0].SOURCE_TYPE = "OUTBOUND_ITEM";
      } else if (sourceDropdownType === "SYSTEM_ITEM" || sourceDropdownType === "SYSTEM_DATA") {
        //system item
        obj.IN_ARG[0].SOURCE_TYPE = "SYSTEM_ITEM";
        newSelNode.IN_ARG[0].SOURCE_TYPE = "SYSTEM_ITEM";
      } else {
        //no source type. remove props
        if (obj.IN_ARG[0].VALUE) delete obj.IN_ARG[0].VALUE;
        if (obj.IN_ARG[0].INDEX) delete obj.IN_ARG[0].INDEX;
        if (obj.IN_ARG[0].ACTION) delete obj.IN_ARG[0].ACTION;        
        if (obj.IN_ARG[0].PARENT) delete obj.IN_ARG[0].PARENT;
        if (obj.IN_ARG[0].MAPNAME) delete obj.IN_ARG[0].MAPNAME;
        if (obj.IN_ARG[0].SOURCE_TYPE) delete obj.IN_ARG[0].SOURCE_TYPE;
        if (obj.IN_ARG[0].DESCRIPTION) delete obj.IN_ARG[0].DESCRIPTION;
        if (obj.IN_ARG[0].RECORD_TYPE) delete obj.IN_ARG[0].RECORD_TYPE;
        if (obj.IN_ARG[0].CUSTOM_DB_FIELD) delete obj.IN_ARG[0].CUSTOM_DB_FIELD;
        if (obj.IN_ARG[0].PLUGIN_NAME) delete obj.IN_ARG[0].PLUGIN_NAME;
        if (obj.IN_ARG[0].WF_ITEM_ID) delete obj.IN_ARG[0].WF_ITEM_ID;
        if (obj.IN_ARG[0].WF_ITEM_LOCATION) delete obj.IN_ARG[0].WF_ITEM_LOCATION;
        if (obj.IN_ARG[0].MAP_REPETITION_INDEX) delete obj.IN_ARG[0].MAP_REPETITION_INDEX;
        if (obj.IN_ARG[0].SOURCE_REPEATING_FIELD) delete obj.IN_ARG[0].SOURCE_REPEATING_FIELD;

        if (newSelNode.IN_ARG[0].VALUE) delete newSelNode.IN_ARG[0].VALUE;
        if (newSelNode.IN_ARG[0].INDEX) delete newSelNode.IN_ARG[0].INDEX;
        if (newSelNode.IN_ARG[0].ACTION) delete newSelNode.IN_ARG[0].ACTION;        
        if (newSelNode.IN_ARG[0].PARENT) delete newSelNode.IN_ARG[0].PARENT;
        if (newSelNode.IN_ARG[0].MAPNAME) delete newSelNode.IN_ARG[0].MAPNAME;
        if (newSelNode.IN_ARG[0].SOURCE_TYPE) delete newSelNode.IN_ARG[0].SOURCE_TYPE;
        if (newSelNode.IN_ARG[0].DESCRIPTION) delete newSelNode.IN_ARG[0].DESCRIPTION;
        if (newSelNode.IN_ARG[0].RECORD_TYPE) delete newSelNode.IN_ARG[0].RECORD_TYPE;
        if (newSelNode.IN_ARG[0].CUSTOM_DB_FIELD) delete newSelNode.IN_ARG[0].CUSTOM_DB_FIELD;
        if (newSelNode.IN_ARG[0].PLUGIN_NAME) delete newSelNode.IN_ARG[0].PLUGIN_NAME;
        if (newSelNode.IN_ARG[0].WF_ITEM_ID) delete newSelNode.IN_ARG[0].WF_ITEM_ID;
        if (newSelNode.IN_ARG[0].WF_ITEM_LOCATION) delete newSelNode.IN_ARG[0].WF_ITEM_LOCATION;
        if (newSelNode.IN_ARG[0].MAP_REPETITION_INDEX) delete newSelNode.IN_ARG[0].MAP_REPETITION_INDEX;
        if (newSelNode.IN_ARG[0].SOURCE_REPEATING_FIELD) delete obj.IN_ARG[0].SOURCE_REPEATING_FIELD;
        
      }

      //write to relevant props
      if (values.MAPNAME) {
        obj.IN_ARG[0].MAPNAME = values.MAPNAME;
        newSelNode.IN_ARG[0].MAPNAME = values.MAPNAME;
      }
      if (values.RECORD_TYPE) {
        obj.IN_ARG[0].RECORD_TYPE = values.RECORD_TYPE;
        newSelNode.IN_ARG[0].RECORD_TYPE = values.RECORD_TYPE;
      }
      if (values.ACTION) {
        obj.IN_ARG[0].ACTION = values.ACTION;
        newSelNode.IN_ARG[0].ACTION = values.ACTION;
      }
      if (values.INDEX) {
        obj.IN_ARG[0].INDEX = values.INDEX;
        newSelNode.IN_ARG[0].INDEX = values.INDEX;
      }
      if (values.PRECISION) {
        obj.IN_ARG[0].PRECISION = values.PRECISION;
        newSelNode.IN_ARG[0].PRECISION = values.PRECISION;
      }
      if (values.PRECISION_OUT) {
        obj.IN_ARG[0].PRECISION_OUT = values.PRECISION_OUT;
        newSelNode.IN_ARG[0].PRECISION_OUT = values.PRECISION_OUT;
      }
      if (values.ROUND) {
        obj.IN_ARG[0].ROUND = values.ROUND;
        newSelNode.IN_ARG[0].ROUND = values.ROUND;
      }
      if (values.PARENT) {
        obj.IN_ARG[0].PARENT = values.PARENT ? values.PARENT : "NONE";
        newSelNode.IN_ARG[0].PARENT = values.PARENT ? values.PARENT : "NONE";
      }
      if (values.CUSTOM_DB_FIELD) {
        obj.IN_ARG[0].CUSTOM_DB_FIELD = values.CUSTOM_DB_FIELD;
        newSelNode.IN_ARG[0].CUSTOM_DB_FIELD = values.CUSTOM_DB_FIELD;
        if (obj.IN_ARG[0].PLUGIN_NAME) delete obj.IN_ARG[0].PLUGIN_NAME;
      }
      if (values.PLUGIN_NAME) {
        obj.IN_ARG[0].PLUGIN_NAME = values.PLUGIN_NAME;
        newSelNode.IN_ARG[0].PLUGIN_NAME = values.PLUGIN_NAME;
        if (obj.IN_ARG[0].CUSTOM_DB_FIELD) delete obj.IN_ARG[0].CUSTOM_DB_FIELD;
      }
      if (values.MAP_REPETITION_INDEX) {
        obj.IN_ARG[0].MAP_REPETITION_INDEX = values.MAP_REPETITION_INDEX;
        newSelNode.IN_ARG[0].MAP_REPETITION_INDEX = values.MAP_REPETITION_INDEX;
      }
      if (values.SOURCE_REPEATING_FIELD) {
        obj.IN_ARG[0].SOURCE_REPEATING_FIELD = values.SOURCE_REPEATING_FIELD;
        newSelNode.IN_ARG[0].SOURCE_REPEATING_FIELD = values.SOURCE_REPEATING_FIELD;
      }
     }
     return obj;
   });

   //prevent dispatch for no reason
   if (isEqual(translatedData, updatedTranslatedData)) return;

   if (newSelNode) {
    dispatch(
      treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: newSelNode,
      })
    );
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedTranslatedData,
      })
    );
   }
 }

  const handleNameChange = (val) => {
    if (loaded) setSequenceName(val);
  }

  const handleGroupChange = (val) => {
    if (val?.length > 30) {
      if (loaded) setSequenceGroup(val.substring(0, 30));
    } else {
      if (loaded) setSequenceGroup(val || "");
    }
  }

  const handleFieldTypeChange = (e) => {
    if (loaded) setFieldType(e.target.value);
  }

  const handleConstantValueChange = (val) => {
    if (loaded) setConstantValue(val);
  }

  const handleIncrementByChange = (val) => {
    if (loaded) setIncrementBy(val);
  }

  const handleMaxValueChange = (val) => {
    if (loaded) setMaxValue(val);
  }

  const handleFieldtypeChange = (val) => {
    // console.log(val);
  }

  useEffect(() => {
    setInitialInputVals();
  }, [selectedNode?.key]);

  useEffect(() => {
    if (loaded) return debounceFunction(() => storeInputVal("NAME", sequenceName));
  }, [sequenceName]);

  useEffect(() => {;
    if (loaded) return debounceFunction(() => storeInputVal("GROUP", sequenceGroup));
  }, [sequenceGroup]);

  useEffect(() => {
    handleFieldtypeChange(fieldType);
    if (loaded) return debounceFunction(() => storeInputVal("FIELDTYPE", fieldType));
  }, [fieldType]);

  useEffect(() => {
    if (loaded) return debounceFunction(() => storeInputVal("VALUE", constantValue));
  }, [constantValue]);

  useEffect(() => {
    if (loaded) return debounceFunction(() => storeInputVal("INCREMENT_BY", incrementBy));
  }, [incrementBy]);

  useEffect(() => {
    if (loaded) return debounceFunction(() => storeInputVal("MAX_VALUE", maxValue));
  }, [maxValue]);

  useEffect(() => {
    if (loaded) return debounceFunction(() => storeInputVal("TYPE", type));
  }, [type]);

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
    }
  }, [loaded]);

  return (<div style={{padding: "0 8px 16px 16px"}}>
    <div style={{marginBottom: "16px", display: "flex", flexWrap: "wrap"}}>
      <FormControl variant="outlined" sx={{minWidth: "75px", width: "unset", marginTop: "16px", marginRight: "8px"}} size="small">
        <InputLabel id="ftDropdownLabel">{t("LBL_FLD_TYPE")}</InputLabel>
        <Select
          sx={{height: "34px", width: "unset"}}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
              },
            },
          }}
          variant="outlined"
          size="small"
          name={"FIELDTYPE"}
          value={fieldType}
          labelId="ftDropdownLabel"
          label={t("LBL_FLD_TYPE")}
          id="ftDropdown"
          onChange={handleFieldTypeChange}
        >
          {fieldTypeOptionsDropdown}
        </Select>
      </FormControl>
      <FormControl  size="small" sx={{marginRight: "8px", flex: "1", minWidth: "110px", marginTop: "16px"}}>
        <TextField
          variant="outlined"
          size="small"
          autoComplete="off"
          label={t("LBL_SEQ_NAME").replace(":", "")}
          className={"width-100"}
          name={"LBL_SEQ_NAME"}
          value={sequenceName}
          onChange={(e) => {
            handleNameChange(e.target.value, e)
          }}
        />
      </FormControl>

      <FormControl  size="small" sx={{marginRight: "8px", flex: "1", minWidth: "110px", marginTop: "16px"}}>
        <TextField
          variant="outlined"
          size="small"
          autoComplete="off"
          label={t("LBL_SEQ_GROUP").replace(":", "")}
          className={"width-100"}
          name={"LBL_SEQ_GROUP"}
          value={sequenceGroup}
          onChange={(e) => {
            handleGroupChange(e.target.value, e)
          }}
        />
      </FormControl>
    </div>
    
    <div style={{paddingRight: "8px"}}>
      {(selectedNode && selectedNode.IN_ARG && selectedNode.IN_ARG[0] && selectedNode.IN_ARG[0].FIELDTYPE && selectedNode.IN_ARG[0].FIELDTYPE === "PROPERTY") && (
        <FormControl  size="small" sx={{flex: "1", minWidth: "110px", width: "100%"}}>
          <TextField
            variant="outlined"
            size="small"
            autoComplete="off"
            label={t("CONSTANT_VALUE")}
            className={"width-100"}
            name={"CONSTANT_VALUE"}
            value={constantValue}
            onChange={(e) => {
              handleConstantValueChange(e.target.value, e)
            }}
          />
        </FormControl>
      )}
    </div>
    

    {/* Data Source Section */}
    {((selectedNode && selectedNode.IN_ARG && selectedNode.IN_ARG[0] && selectedNode.IN_ARG[0].FIELDTYPE &&  selectedNode.IN_ARG[0].FIELDTYPE !== "PROPERTY") || !selectedNode.IN_ARG) && 
      <div style={{marginRight: "8px"}}>
        <SourceType
          node={{
            PARENT: selectedNode?.IN_ARG?.[0]?.PARENT ? selectedNode.IN_ARG[0].PARENT : "",
            MAPNAME: selectedNode?.IN_ARG?.[0]?.MAPNAME ? selectedNode.IN_ARG[0].MAPNAME : "",
            RECORD_TYPE: selectedNode?.IN_ARG?.[0]?.RECORD_TYPE ? selectedNode.IN_ARG[0].RECORD_TYPE : "",
            key: selectedNode?.IN_ARG?.[0]?.key ? selectedNode.IN_ARG[0].key : "",
            ACTION: selectedNode?.IN_ARG?.[0]?.ACTION ? selectedNode?.IN_ARG?.[0]?.ACTION : "",
						INDEX: selectedNode?.IN_ARG?.[0]?.INDEX ? selectedNode?.IN_ARG?.[0]?.INDEX : "",
            MAP_REPETITION_INDEX: selectedNode?.IN_ARG?.[0]?.MAP_REPETITION_INDEX ? selectedNode.IN_ARG[0].MAP_REPETITION_INDEX : "",
            CUSTOM_DB_FIELD: selectedNode?.IN_ARG?.[0]?.CUSTOM_DB_FIELD ? selectedNode.IN_ARG[0].CUSTOM_DB_FIELD : "",
            PLUGIN_NAME: selectedNode?.IN_ARG?.[0]?.PLUGIN_NAME ? selectedNode.IN_ARG[0].PLUGIN_NAME : ""
          }}
          valuesHandler={sourceTypeValueChange}
          // callerComponent='recordProperties'
        />
      </div>
    }

    <div style={{display: "flex", flexWrap: "wrap"}}>
      <FormControl  size="small" sx={{marginRight: "8px", flex: "1", minWidth: "110px", marginTop: "16px"}}>
        <TextField
          variant="outlined"
          size="small"
          autoComplete="off"
          label={t("LBL_INC_BY").replace(":", "")}
          className={"width-100"}
          name={"LBL_INC_BY"}
          value={incrementBy}
          onChange={(e) => {
            handleIncrementByChange(e.target.value, e)
          }}
        />
      </FormControl>
      <FormControl  size="small" sx={{marginRight: "8px", flex: "1", minWidth: "110px", marginTop: "16px"}}>
        <TextField
          variant="outlined"
          size="small"
          autoComplete="off"
          label={t("LBL_MAX_VAL").replace(":", "")}
          className={"width-100"}
          name={"LBL_MAX_VAL"}
          value={maxValue}
          onChange={(e) => {
            handleMaxValueChange(e.target.value, e)
          }}
        />
      </FormControl>
    </div>
    <div style={{marginRight: "8px"}}>
      <DefaultCheckbox 
        label={t("BTN_INC_ONLY_ONCE")}
        name={"BTN_INC_ONLY_ONCE"}
        value={type}
        setValue={(val) => {setType(val)}}
      />
    </div>
  </div>)
}

const styles = {
  fpContainer: {
    // padding: "0 16px 16px 16px"
    padding: "0 4px 4px 4px"
  },
  accordianContainer: {
    border: "none",
    padding: "0",
    margin: "0",
    boxShadow: "none",
    backgroundColor: "unset"
  },
  accordianHeader: {
    border: "none",
    padding: "0",
    margin: "0",
    backgroundColor: "unset"
  },
  accordianContent: {
    border: "none",
    padding: "0 15px",
    margin: "0",
    backgroundColor: "unset"
  }
}

const fieldTypeOptionsDropdown = [
  <MenuItem key={1} value={"SELECT"}>{t("SELECT")}</MenuItem>,
  <MenuItem key={2} value={"ALPHA"}>{t("ALPHA")}</MenuItem>,
  <MenuItem key={3} value={"BIT_FLAG"}>{t("BIT_FLAG")}</MenuItem>,
  <MenuItem key={4} value={"PROPERTY"}>{t("CONSTANT")}</MenuItem>,
  <MenuItem key={5} value={"DATE"}>{t("DATE")}</MenuItem>,
  <MenuItem key={6} value={"FLOAT"}>{t("FLOAT")}</MenuItem>,
  <MenuItem key={7} value={"INTEGER"}>{t("INTEGER")}</MenuItem>,
  <MenuItem key={8} value={"PACKED_INT"}>{t("PACKED_INT")}</MenuItem>,
  <MenuItem key={9} value={"ZONED_INT"}>{t("ZONED_INT")}</MenuItem>,
];